import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { X, Gift, Sparkles, Lightbulb, CheckCircle2, Heart } from "lucide-react";

const SESSION_KEY = "prx_exit_suggest_shown";
const AB_KEY = "prx_exit_ab";

// Deux accroches testées en A/B pour mesurer laquelle convertit le mieux
const VARIANTS = {
  A: {
    badge: "Avant de partir…",
    title: "Une phrase, et vos bons plans sont gratuits 🎁",
    body: (
      <>Dites-nous en une ligne ce qui vous ferait revenir sur PROXIreduc-28.
      En échange, on débloque votre <strong>accès gratuit</strong> à toutes les réductions d'Eure-et-Loir.</>
    ),
    placeholder: "Ex : j'aimerais plus de bons plans restaurants à Dreux…",
    cta: "Je débloque mes bons plans gratuits",
  },
  B: {
    badge: "Offert · 30 secondes",
    title: "Il vous manque un bon plan près de chez vous ?",
    body: (
      <>Dites-nous en une phrase le commerce ou la réduction qui vous ferait plaisir.
      On vous crée un <strong>accès gratuit</strong> et on vous prévient dès qu'il arrive.</>
    ),
    placeholder: "Ex : une réduction chez le coiffeur à Chartres…",
    cta: "Oui, je veux mes bons plans gratuits",
  },
};

function pickVariant() {
  let v = localStorage.getItem(AB_KEY);
  if (v !== "A" && v !== "B") {
    v = Math.random() < 0.5 ? "A" : "B";
    localStorage.setItem(AB_KEY, v);
  }
  return v;
}

export default function ExitIntentSuggestion() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(true);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [variant] = useState(pickVariant);
  const armed = useRef(false);

  useEffect(() => {
    if (user !== false) return; // n'afficher qu'aux visiteurs non connectés
    if (sessionStorage.getItem(SESSION_KEY)) return;

    // Pages de contenu : on déclenche aussi sur engagement (temps / scroll profond)
    const isContentPage = /^\/(blog|sortir|offre|commerce)\//.test(window.location.pathname);

    const trigger = () => {
      if (armed.current || sessionStorage.getItem(SESSION_KEY)) return;
      armed.current = true;
      sessionStorage.setItem(SESSION_KEY, "1");
      setOpen(true);
      api.post("/suggestions/ab-event", { variant }).catch(() => {});
    };

    // Desktop : la souris quitte le haut de la fenêtre (intention de fermeture/onglet)
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) trigger();
    };
    // Scroll : remontée rapide (mobile) OU lecture profonde d'une page de contenu
    let lastY = window.scrollY;
    let canScrollTrigger = false;
    const onScroll = () => {
      const y = window.scrollY;
      if (canScrollTrigger && lastY - y > 90 && y < 260) trigger();
      if (isContentPage) {
        const docH = document.documentElement.scrollHeight - window.innerHeight;
        if (docH > 400 && y / docH > 0.6) trigger();
      }
      lastY = y;
    };
    const scrollArm = setTimeout(() => { canScrollTrigger = true; }, 4000);
    // Repli temporel : 30 s sur mobile, 35 s sur une page de contenu desktop
    const timeFallback = setTimeout(() => {
      if (window.matchMedia("(max-width: 768px)").matches || isContentPage) trigger();
    }, isContentPage ? 35000 : 30000);

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(scrollArm);
      clearTimeout(timeFallback);
    };
  }, [user, variant]);

  const close = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !message.trim()) {
      toast.error("Indiquez votre email et votre idée en une phrase.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/suggestions", {
        email: email.trim(), message: message.trim(), profile: "Visiteur",
        gdpr_consent: consent, origin_page: window.location.pathname + " (exit-intent)",
        ab_variant: variant,
      });
      setDone(r.data || { ok: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'envoyer votre suggestion.");
    } finally { setLoading(false); }
  };

  if (!open) return null;

  const v = VARIANTS[variant] || VARIANTS.A;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="exit-intent-overlay" data-ab-variant={variant}>
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-testid="exit-intent-modal">
        <button onClick={close} aria-label="Fermer" data-testid="exit-intent-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 grid place-items-center rounded-full bg-white/80 text-slate-500 hover:bg-white hover:text-slate-800 transition-colors">
          <X size={18} />
        </button>

        {done ? (
          <div className="p-7 text-center" data-testid="exit-intent-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
              <CheckCircle2 size={30} />
            </div>
            <h2 className="mt-4 font-heading text-2xl font-extrabold text-slate-900">Merci, c'est noté ! 💡</h2>
            {done.account_created ? (
              <>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  Votre <strong>accès gratuit</strong> aux bons plans vient d'être créé. Vos identifiants
                  viennent de partir par e-mail (pensez aux spams).
                </p>
                <Link to="/connexion" onClick={close}>
                  <Button className="mt-5 btn-shine text-white h-11 w-full font-bold" data-testid="exit-intent-go-login">
                    <Heart size={16} className="mr-2" /> Je découvre mes bons plans
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  Nous lisons chaque idée et vous répondrons à <strong>{email}</strong>.
                </p>
                <Button variant="outline" onClick={close} className="mt-5 h-11 w-full" data-testid="exit-intent-done-close">
                  Continuer ma visite
                </Button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-br from-[#E05638] to-[#f0943a] text-white p-6 pt-7">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 text-[11px] font-bold uppercase tracking-widest">
                <Sparkles size={13} /> {v.badge}
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-[26px] font-extrabold leading-tight flex items-start gap-2">
                <Lightbulb size={26} className="shrink-0 mt-0.5" />
                {v.title}
              </h2>
              <p className="mt-2 text-white/90 text-sm leading-relaxed">
                {v.body}
              </p>
            </div>

            <form onSubmit={submit} className="p-6 space-y-3" data-testid="exit-intent-form">
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Votre email" data-testid="exit-intent-email" className="h-11" />
              <Textarea rows={3} required value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder={v.placeholder} data-testid="exit-intent-message" />
              <label className="flex items-start gap-2 cursor-pointer" data-testid="exit-intent-consent-wrapper">
                <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4" data-testid="exit-intent-consent" />
                <span className="text-[11px] text-slate-500 leading-snug">
                  J'accepte la création d'un compte gratuit pour recevoir ma réponse et mes accès, selon la{" "}
                  <a href="/confidentialite" target="_blank" rel="noreferrer" className="text-[#E05638] underline">politique de confidentialité</a>.
                </span>
              </label>
              <Button type="submit" disabled={loading || !consent} className="w-full btn-shine text-white h-12 text-[15px] font-extrabold" data-testid="exit-intent-submit">
                <Gift size={17} className="mr-2 shrink-0" /> {loading ? "Envoi…" : v.cta}
              </Button>
              <button type="button" onClick={close} className="w-full text-center text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="exit-intent-decline">
                Non merci, je continue sans mes bons plans
              </button>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
